import { enumFromJson, enumToJson, type JsonValue } from "@bufbuild/protobuf";
import { DataType, DataTypeSchema, type DataTypeJson } from "#gen/common/v1/types_pb.js";
import { PostalAddressSchema } from "#gen/google/type/postal_address_pb.js";
import { formattedAddress } from "#lib/address.js";
import { formattedArray } from "#lib/array.js";
import AddressInput from "#lib/components/flow/editor/data-types/inputs/AddressInput.svelte";
import ArrayInput from "#lib/components/flow/editor/data-types/inputs/ArrayInput.svelte";
import BooleanInput from "#lib/components/flow/editor/data-types/inputs/BooleanInput.svelte";
import ImageInput from "#lib/components/flow/editor/data-types/inputs/ImageInput.svelte";
import NumberInput from "#lib/components/flow/editor/data-types/inputs/NumberInput.svelte";
import PhoneInput from "#lib/components/flow/editor/data-types/inputs/PhoneInput.svelte";
import TextInput from "#lib/components/flow/editor/data-types/inputs/TextInput.svelte";
import { isEmptyDataTypeInputValue, type DataTypeInputValue, type InputControl } from "#lib/components/flow/editor/data-types/inputs/dataTypeInput.js";
import type { InstanceType, Schema } from "#lib/components/jsonForms/jsonSchema.js";
import { formattedNumber } from "#lib/number.js";
import { fromJson } from "#lib/protobuf-compat.js";
import { sentenceCase } from "#lib/string.js";
import { normalizeUrl } from "#lib/url.js";

export const X_FLOWCODE_DATA_TYPE_KEY = "x-flowcode-data-type" as const;

export type InputNormalizer = (value: string) => string;

export function getDataTypeString(dataType: DataType): string {
	return sentenceCase(DataType[dataType]);
}

interface DataTypeItem {
	name: string;
	value: DataType;
	schemaType: InstanceType;
	schemaFormat?: string;
	schemaTitle?: string;
	inputControl: InputControl;
	inputNormalizer?: InputNormalizer;
}

export const dataTypeItems: DataTypeItem[] = [
	{
		name: getDataTypeString(DataType.STRING),
		value: DataType.STRING,
		schemaType: "string",
		inputControl: { component: TextInput },
	},
	{
		name: getDataTypeString(DataType.EMAIL),
		value: DataType.EMAIL,
		schemaType: "string",
		schemaFormat: "email",
		inputControl: { component: TextInput, attrs: { type: "email", autocomplete: "email", inputmode: "email" } },
	},
	{
		name: getDataTypeString(DataType.PHONE),
		value: DataType.PHONE,
		schemaType: "string",
		inputControl: { component: PhoneInput },
	},

	{
		name: getDataTypeString(DataType.DATE),
		value: DataType.DATE,
		schemaType: "string",
		schemaFormat: "date",
		inputControl: { component: TextInput, attrs: { type: "date" } },
	},

	{
		name: getDataTypeString(DataType.URL),
		value: DataType.URL,
		schemaType: "string",
		schemaFormat: "uri",
		inputControl: { component: TextInput, attrs: { type: "url", autocomplete: "url", inputmode: "url" } },
		inputNormalizer: normalizeUrl,
	},

	{
		name: getDataTypeString(DataType.IMAGE),
		value: DataType.IMAGE,
		schemaType: "object",
		inputControl: { component: ImageInput },
	},

	{
		name: getDataTypeString(DataType.BOOLEAN),
		value: DataType.BOOLEAN,
		schemaType: "boolean",
		inputControl: { component: BooleanInput },
	},

	{
		name: getDataTypeString(DataType.ADDRESS),
		value: DataType.ADDRESS,
		schemaType: "object",
		inputControl: { component: AddressInput },
	},

	{
		name: getDataTypeString(DataType.NUMBER),
		value: DataType.NUMBER,
		schemaType: "number",
		inputControl: { component: NumberInput, attrs: { type: "number" } },
	},

	{
		name: getDataTypeString(DataType.NAME),
		value: DataType.NAME,
		schemaType: "string",
		inputControl: { component: TextInput, attrs: { type: "text", autocomplete: "name", inputmode: "text" } },
	},

	{
		name: "Multiple strings", // custom name per spec
		value: DataType.ARRAY,
		schemaType: "array",
		inputControl: { component: ArrayInput },
	},
];

export function schemaTypeForDataType(dataType: DataType): InstanceType {
	return dataTypeItems.find(i => i.value === dataType)?.schemaType ?? "string";
}

function schemaFormatForDataType(dataType: DataType): string | undefined {
	return dataTypeItems.find(i => i.value === dataType)?.schemaFormat;
}

function schemaXFlowcodeDataTypeForDataType(dataType: DataType): DataTypeJson {
	return enumToJson(DataTypeSchema, dataType) as DataTypeJson;
}

export function createSchema(dataType: DataType, title?: string, description?: string): Schema {
	const field: Schema = {
		title: title?.length ? title : schemaTitleForDataType(dataType),
		type: schemaTypeForDataType(dataType),
		[X_FLOWCODE_DATA_TYPE_KEY]: schemaXFlowcodeDataTypeForDataType(dataType),
	};

	const trimmedDescription = description?.trim();
	if (trimmedDescription) {
		field.description = trimmedDescription;
	}

	if (dataType === DataType.ADDRESS) {
		field.properties = {
			regionCode: { type: "string" },
			addressLines: { type: "array", items: { type: "string" } },
			locality: { type: "string" },
			administrativeArea: { type: "string" },
			postalCode: { type: "string" },
		};
		return field;
	}

	if (dataType === DataType.IMAGE) {
		field.properties = {
			value: { type: "string", format: "uri" },
			contentType: { type: ["string", "null"] },
			focalPoint: {
				type: "object",
				properties: {
					x: { type: "number" },
					y: { type: "number" },
				},
			},
		};
		field.required = ["value"];
		return field;
	}

	const format = schemaFormatForDataType(dataType);
	if (format !== undefined) {
		field.format = format;
	}

	return field;
}

function schemaTitleForDataType(dataType: DataType): string {
	const item = dataTypeItems.find(i => i.value === dataType);
	return item?.schemaTitle ?? item?.name ?? "Field";
}

export function dataTypeForSchema(schema: Schema | undefined): DataType {
	const xFlowcodeDataType = schema?.[X_FLOWCODE_DATA_TYPE_KEY];
	if (xFlowcodeDataType === undefined || xFlowcodeDataType === null) {
		if (schema) {
			schema[X_FLOWCODE_DATA_TYPE_KEY] = schemaXFlowcodeDataTypeForDataType(DataType.STRING);
		}
		return DataType.STRING;
	}
	return enumFromJson(DataTypeSchema, xFlowcodeDataType as DataTypeJson);
}

function dataTypeItemForSchema(schema: Schema | undefined): DataTypeItem | undefined {
	return dataTypeItems.find(i => i.value === dataTypeForSchema(schema));
}

export function inputComponentForDataType(dataType: DataType): InputControl {
	return dataTypeItems.find(i => i.value === dataType)?.inputControl ?? { component: TextInput };
}

export function inputNormalizerForSchema(schema: Schema | undefined): InputNormalizer | undefined {
	return dataTypeItemForSchema(schema)?.inputNormalizer;
}

export function formatResolvedDataPropertyValue(entryMetadata: Record<string, unknown> | undefined, key: string, batchMetadata: Record<string, unknown> | undefined, schema: Schema): string {
	let value: unknown;
	if (entryMetadata !== undefined && Object.hasOwn(entryMetadata, key)) {
		const entryValue = entryMetadata[key];
		const dataType = dataTypeForSchema(schema);
		let inputValue: DataTypeInputValue;
		if (dataType === DataType.ADDRESS || dataType === DataType.IMAGE) {
			inputValue = entryValue as DataTypeInputValue;
		} else if (dataType === DataType.BOOLEAN) {
			inputValue = typeof entryValue === "boolean" ? entryValue : undefined;
		} else if (dataType === DataType.ARRAY) {
			inputValue = Array.isArray(entryValue) ? entryValue.filter((item): item is string => typeof item === "string") : [];
		} else if (typeof entryValue === "string") {
			inputValue = entryValue;
		} else if (dataType === DataType.NUMBER) {
			inputValue = entryValue as DataTypeInputValue;
		} else {
			inputValue = "";
		}
		if (isEmptyDataTypeInputValue(inputValue)) {
			return "";
		}
		value = entryValue;
	} else if (batchMetadata !== undefined && Object.hasOwn(batchMetadata, key)) {
		value = batchMetadata[key];
	}

	if (value === undefined || value === null || value === "") {
		return "-";
	}
	const dataType = dataTypeForSchema(schema);
	switch (dataType) {
		case DataType.ADDRESS:
			return formattedAddress(fromJson(PostalAddressSchema, value as JsonValue));
		case DataType.IMAGE:
			return typeof value === "object" && value !== null && "value" in value && typeof value.value === "string" ? value.value || "-" : "-";
		case DataType.ARRAY:
			return formattedArray(value as JsonValue);
		case DataType.NUMBER:
			return formattedNumber(value as JsonValue);
		default:
			return value as string;
	}
}
